import { createRequestTypes } from '../../helpers/helpers'
import { NotesPanelState } from '../../helpers/notesPanel'
import { createAction } from '../types'
import { IApi } from './../../api/index'
import {
  ADD_NOTE_SUCCESS,
  CLEAR_NOTES_PANEL_FORCE,
  FORCE_OPEN_NOTES_PANEL,
  HIDE_NOTES_PANEL,
  REQUEST_NOTES_COMPOSER,
  SET_NOTES_PANEL_STATE,
  TOGGLE_NOTES,
  TOGGLE_NOTES_OFF,
  TOGGLE_NOTES_ON,
  prefix,
} from './panelActionTypes'

// The panel action types live in `panelActionTypes.ts` (import-free) so the panel
// reducers can be loaded without this module's api/React graph. Re-exported here
// so callers still have a single import site.
export {
  ADD_NOTE_SUCCESS,
  CLEAR_NOTES_PANEL_FORCE,
  FORCE_OPEN_NOTES_PANEL,
  HIDE_NOTES_PANEL,
  REQUEST_NOTES_COMPOSER,
  SET_NOTES_PANEL_STATE,
  TOGGLE_NOTES,
  TOGGLE_NOTES_OFF,
  TOGGLE_NOTES_ON,
  prefix,
}

// Backward-compatible visibility toggles. These map onto the panel-state model:
// on -> 'docked', off -> 'rail', toggle -> flip. `isNotesShown` is derived as
// `notesPanelState === 'docked'`, so existing callers keep working unchanged.
// Use `hideNotesPanel` (not `toggleNotesOff`) to suppress the column entirely.
export const toggleNotes = () => ({
  type: TOGGLE_NOTES,
})

export const toggleNotesOff = () => ({
  type: TOGGLE_NOTES_OFF,
})

export const toggleNotesOn = () => ({
  type: TOGGLE_NOTES_ON,
})

// Explicit panel-state setter used by the context-aware default resolver. Unlike
// the toggles above this carries the target state directly; persistence of a
// user's choice is handled at the call site, not here.
export const setNotesPanelState = (state: NotesPanelState) => ({
  type: SET_NOTES_PANEL_STATE,
  state,
})

// Suppress the column entirely (no rail, no panel) for pages that own the full
// viewport — distinct from `toggleNotesOff`, which collapses to the rail, and
// from `hideNotes`, which is a data reset and touches no visibility state.
export const hideNotesPanel = () => ({
  type: HIDE_NOTES_PANEL,
})

/**
 * Deep-link / flag force-open. Sets the CURRENT state to docked and latches it,
 * so the context-aware resolver leaves it alone until the force is cleared.
 *
 * The latch is what makes `?noteId=` survive: `NotesContainer` strips that param
 * from the URL as soon as it selects the note, so anything deriving "is this a
 * deep link?" from the URL would collapse the panel out from under the reader.
 * It deliberately does NOT write the remembered per-workflow preference — a
 * forced open is a temporary override, not a change of mind.
 */
// `tab` is required: the latch and the tab it asked for are the same piece of state,
// so a force-open with no tab would be a latch that doesn't latch.
export const forceOpenNotesPanel = (tab: string) => ({
  type: FORCE_OPEN_NOTES_PANEL,
  tab,
})

export const clearNotesPanelForce = () => ({
  type: CLEAR_NOTES_PANEL_FORCE,
})

// The rail's "+" is the quick-add prompt: it opens the docked panel with the
// composer already focused so the gesture is "start writing", not "go look".
// (The compose-in-place popover itself is a later phase.) Cleared once the note
// is saved or the panel collapses, so the next "+" focuses again.
export const requestNotesComposer = () => ({
  type: REQUEST_NOTES_COMPOSER,
})

export const ADD_NOTE_REQUEST = `${prefix}/ADD_NOTE_REQUEST`
export const ADD_NOTE_FAILURE = `${prefix}/ADD_NOTE_FAILURE`

export const addNote = createAction((params: object) => ({
  type: [ADD_NOTE_REQUEST, ADD_NOTE_SUCCESS, ADD_NOTE_FAILURE],
  promise: (api: IApi) => api.notes.add(params),
  params,
}))

// Counts for the collapsed rail. Fetched on page mount rather than on panel open
// — a rail that can't say "12 notes, 2 need action" hides the notes it collapsed.
const COUNT_NOTES = createRequestTypes(prefix, 'COUNT_NOTES')
export const [COUNT_NOTES_REQUEST, COUNT_NOTES_SUCCESS, COUNT_NOTES_FAILURE] = COUNT_NOTES

export const countNotes = createAction((id: string, params: object) => ({
  type: [COUNT_NOTES_REQUEST, COUNT_NOTES_SUCCESS, COUNT_NOTES_FAILURE],
  promise: (api: IApi) => api.notes.count(id, params),
}))

// Read receipts. Fired for notes the user actually had on screen, which is what
// makes the unread badge behave like a notification count rather than a total.
const MARK_NOTES_READ = createRequestTypes(prefix, 'MARK_NOTES_READ')
export const [MARK_NOTES_READ_REQUEST, MARK_NOTES_READ_SUCCESS, MARK_NOTES_READ_FAILURE] =
  MARK_NOTES_READ

// `params` carries the surface the notes were read from ({ id, workflow }); the
// backend scopes the ids against it rather than trusting them.
export const markNotesRead = createAction((noteIds: string[], params: object) => ({
  type: [MARK_NOTES_READ_REQUEST, MARK_NOTES_READ_SUCCESS, MARK_NOTES_READ_FAILURE],
  promise: (api: IApi) => api.notes.markRead(noteIds, params),
  noteIds,
  params,
}))

export const LIST_NOTES_REQUEST = `${prefix}/LIST_NOTES_REQUEST`
export const LIST_NOTES_SUCCESS = `${prefix}/LIST_NOTES_SUCCESS`
export const LIST_NOTES_FAILURE = `${prefix}/LIST_NOTES_FAILURE`

export const listNotes = createAction((id: string, params: object) => ({
  type: [LIST_NOTES_REQUEST, LIST_NOTES_SUCCESS, LIST_NOTES_FAILURE],
  promise: (api: IApi) => api.notes.list(id, params),
  // `id` is carried on the action (not just closed over) so the reducer can
  // record the last list context for a silent Arc-write refetch.
  id,
  params,
}))

export const DELETE_NOTE_REQUEST = `${prefix}/DELETE_NOTE_REQUEST`
export const DELETE_NOTE_SUCCESS = `${prefix}/DELETE_NOTE_SUCCESS`
export const DELETE_NOTE_FAILURE = `${prefix}/DELETE_NOTE_FAILURE`

export const deleteNote = createAction((id: string, params?: object) => ({
  type: [DELETE_NOTE_REQUEST, DELETE_NOTE_SUCCESS, DELETE_NOTE_FAILURE],
  promise: (api: IApi) => api.notes.delete(id),
  params,
}))

export const UPDATE_NOTE_REQUEST = `${prefix}/UPDATE_NOTE_REQUEST`
export const UPDATE_NOTE_SUCCESS = `${prefix}/UPDATE_NOTE_SUCCESS`
export const UPDATE_NOTE_FAILURE = `${prefix}/UPDATE_NOTE_FAILURE`

export const updateNote = createAction((id: string, params: object) => ({
  type: [UPDATE_NOTE_REQUEST, UPDATE_NOTE_SUCCESS, UPDATE_NOTE_FAILURE],
  promise: (api: IApi) => api.notes.update(id, params),
  params,
}))

export const HIDE_NOTES = `${prefix}/HIDE_NOTES`

export const hideNotes = () => ({
  type: HIDE_NOTES,
})

const EXPORT_NOTES = createRequestTypes(prefix, 'EXPORT_NOTES')
export const [EXPORT_NOTES_REQUEST, EXPORT_NOTES_SUCCESS, EXPORT_NOTES_FAILURE] = EXPORT_NOTES

export const exportNotes = createAction((id: string, params: object) => ({
  type: [EXPORT_NOTES_REQUEST, EXPORT_NOTES_SUCCESS, EXPORT_NOTES_FAILURE],
  promise: (api: IApi) =>
    api.notes.list(id, {
      ...params,
      isFile: true,
    }),
  params,
}))

export const GET_ARC_PROGRESS_REQUEST = `${prefix}/GET_ARC_PROGRESS_REQUEST`
export const GET_ARC_PROGRESS_SUCCESS = `${prefix}/GET_ARC_PROGRESS_SUCCESS`
export const GET_ARC_PROGRESS_FAILURE = `${prefix}/GET_ARC_PROGRESS_FAILURE`

// Rehydrate Arc's in-progress reasoning for a note thread (root note id) so a
// viewer opening/refreshing the note mid-run sees the steps so far. `id` rides
// on the action so the reducer can key the result to the right note.
export const getArcProgress = createAction((id: string) => ({
  type: [GET_ARC_PROGRESS_REQUEST, GET_ARC_PROGRESS_SUCCESS, GET_ARC_PROGRESS_FAILURE],
  promise: (api: IApi) => api.notes.arcProgress(id),
  id,
}))
